import { useState, useMemo } from 'react';
import type { CurrentRead } from '@signalvote/data';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { CanvasserType } from '@signalvote/shared';
import type { MyStatsResponse, ShiftListResponse } from '@signalvote/shared';
import type { ContactAnalyticsResponse } from '@signalvote/shared';
import { calculateShiftHours, formatShiftDate } from '@/lib/utils';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { ContactOutcomeChart } from './ContactOutcomeChart';
import type { SortDirection } from './types';

// ============================================================================
// Types
// ============================================================================

interface CanvasserSelfViewProps {
  myStats: MyStatsResponse | undefined;
  shifts: CurrentRead<ShiftListResponse>;
  contactAnalytics: ContactAnalyticsResponse | undefined;
  currentUserId: string;
  isLoadingStats: boolean;
  isLoadingContacts: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CanvasserSelfView({
  myStats,
  shifts,
  contactAnalytics,
  currentUserId,
  isLoadingStats,
  isLoadingContacts,
}: CanvasserSelfViewProps) {
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedShifts = useMemo(() => {
    if (shifts.status !== 'ready') return [];
    return [...shifts.data.items].sort((a, b) => {
      const cmp = a.date.localeCompare(b.date);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [shifts, sortDirection]);

  const handleSortToggle = () => {
    setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const isVolunteer = myStats?.canvasserType === CanvasserType.Volunteer;

  // Loading state
  if (isLoadingStats || !myStats) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Personal Stats Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Shifts Completed</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.shiftsCompleted}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Hours Canvassed</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.hoursCanvassed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Doors Knocked</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.doorsKnocked}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Contacts Made</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.contactsMade}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Success Rate</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.contactSuccessRate}%</p>
          </CardContent>
        </Card>
        {/* ACS-007: Off-shift contacts card. Neutral muted background distinguishes
            it from the comparative cards — it does not feed the leaderboard rank. */}
        <Card data-testid="off-shift-card" className="bg-muted/40">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Off-shift contacts</p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{myStats.offShiftContactsCount}</p>
            <p className="mt-1 text-xs text-muted-foreground">Logged outside a shift</p>
          </CardContent>
        </Card>
      </div>

      {/* My Shifts Table */}
      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold">My Shifts</h3>

        {shifts.status === 'failed' || shifts.status === 'offline' ? (
          <p role="alert" className="py-8 text-center text-sm text-destructive">
            {shifts.status === 'offline' ? "You're offline, so shifts can't load." : "Couldn't load shifts. Try again shortly."}
          </p>
        ) : shifts.status !== 'ready' ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : sortedShifts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No shift data</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th
                    className="cursor-pointer pb-2 pr-4 hover:text-foreground"
                    onClick={handleSortToggle}
                  >
                    Date {sortDirection === 'desc' ? '↓' : '↑'}
                  </th>
                  <th className="pb-2 pr-4">Walkbook</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4 text-right">Hours</th>
                  <th className="pb-2 pr-4 text-right">Doors</th>
                  <th className="pb-2 text-right">Contacts</th>
                </tr>
              </thead>
              <tbody>
                {sortedShifts.map((shift) => {
                  const hours = calculateShiftHours(shift.startedAt, shift.completedAt);
                  return (
                    <tr key={shift.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 tabular-nums">{formatShiftDate(shift.date)}</td>
                      <td className="py-2 pr-4">{shift.walkbookName ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {shift.effectiveStatus === 'NoShow' ? (
                          <Badge variant="destructive">No-Show</Badge>
                        ) : (
                          shift.effectiveStatus
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {hours !== null ? hours.toFixed(1) : '—'}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {shift.doorsKnocked ?? 0}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {shift.contactsMade ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* My Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>My Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {!contactAnalytics?.contactsOverTime?.length ? (
            <p className="text-sm text-muted-foreground">No contact activity data</p>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={contactAnalytics.contactsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) =>
                      new Date(d).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    }
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(d) =>
                      new Date(d).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Outcomes */}
      <ContactOutcomeChart
        contactsByStatus={contactAnalytics?.contactsByStatus ?? []}
        isLoading={isLoadingContacts}
      />

      {/* Volunteer Leaderboard Position */}
      {isVolunteer && myStats.nearbyLeaderboard && (
        <Card>
          <CardHeader>
            <CardTitle>Volunteer Leaderboard</CardTitle>
            <p className="text-sm text-muted-foreground">
              Your position: <span className="font-semibold">#{myStats.leaderboardPosition}</span>
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                {myStats.nearbyLeaderboard.map((entry) => (
                  <tr
                    key={entry.userId}
                    role="row"
                    className={
                      entry.userId === currentUserId
                        ? 'bg-primary/10 font-semibold'
                        : ''
                    }
                  >
                    <td className="py-2 pr-4 tabular-nums">#{entry.rank}</td>
                    <td className="py-2 pr-4">{entry.name}</td>
                    <td className="py-2 text-right tabular-nums">{entry.doorsKnocked} doors</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
